import React, { useEffect, useRef, memo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { createCustomMarker, createDynamicPopup, loadAdditionalDataAsync } from '../../utils/poiUtils.js';
import { CATEGORY_ICON } from '../../shared/constants.js';

function POIMarkers({ pois, onShowOSMInfo, onEnrichPOI }) {
    const map = useMap();
    const markersRef = useRef(new Map()); // featureId → marker
    const onShowOSMInfoRef = useRef(onShowOSMInfo);
    const onEnrichPOIRef = useRef(onEnrichPOI);

    useEffect(() => { onShowOSMInfoRef.current = onShowOSMInfo; }, [onShowOSMInfo]);
    useEffect(() => { onEnrichPOIRef.current = onEnrichPOI; }, [onEnrichPOI]);

    useEffect(() => {
        if (!map) return;

        const newPoisMap = new Map();

        if (Array.isArray(pois)) {
            pois.forEach(item => {
                const poi = item?.poi;
                if (!poi?.geometry?.coordinates) return;
                const props = poi.properties || {};
                const [lng, lat] = poi.geometry.coordinates;
                if (typeof lat !== 'number' || typeof lng !== 'number') return;

                const featureId =
                    props['@id'] ||
                    poi.id ||
                    `poi_${lat.toFixed(6)}_${lng.toFixed(6)}`;

                newPoisMap.set(featureId, { poi, props, lat, lng, featureId });
            });
        }

        // 1. Rimuovi marker non più presenti
        markersRef.current.forEach((marker, id) => {
            if (!newPoisMap.has(id)) {
                map.removeLayer(marker);
                markersRef.current.delete(id);
            }
        });

        // 2. Aggiungi solo i marker nuovi (non già presenti)
        newPoisMap.forEach(({ poi, props, lat, lng, featureId }) => {
            if (markersRef.current.has(featureId)) return; // già esiste, skip

            try {
                const customIcon = createCustomMarker(props, CATEGORY_ICON);
                const marker = L.marker([lat, lng], { icon: customIcon });

                const popupContent = createDynamicPopup(
                    props, lat, lng, featureId, poi,
                    {
                        onShowFullInfo: (_id, poiData) => onShowOSMInfoRef.current?.(poiData),
                        onAddUpdate: (_id, poiData) => onEnrichPOIRef.current?.(poiData)
                    }
                );

                marker.bindPopup(popupContent, { maxWidth: 400, minWidth: 300 });

                marker.on('popupopen', () => {
                    const popupEl = marker.getPopup().getElement();
                    const container = popupEl?.querySelector('.poi-popup-dynamic');
                    if (container && container.dataset.needsLoading === 'true') {
                        container.dataset.needsLoading = 'false';
                        const btnAddUpdate = container.querySelector('.btn-add-update-info');
                        loadAdditionalDataAsync(
                            parseFloat(container.dataset.lat),
                            parseFloat(container.dataset.lng),
                            featureId, container, btnAddUpdate
                        );
                    }
                });

                marker.addTo(map);
                markersRef.current.set(featureId, marker);
            } catch (err) {
                console.error('❌ Errore marker POI:', err);
            }
        });

    }, [pois, map]);

    // Cleanup totale allo smontaggio
    useEffect(() => {
        return () => {
            markersRef.current.forEach(marker => map?.removeLayer(marker));
            markersRef.current.clear();
        };
    }, [map]);

    return null;
}

export default memo(POIMarkers);